import { useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import {
  animationFrameScheduler,
  defaultIfEmpty,
  filter,
  finalize,
  fromEvent,
  last,
  map,
  observeOn,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { type Intersection, Vector2, Vector3 } from 'three';

import { useSubscriptions } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import {
  useEditingLabelBoxStore,
  useToolMainCameraStore,
  useToolMenuStore,
} from '@/store';
import type { NEW__LabelingBox } from '@/util';
import { filterOrRestart, subVector, withCanvasXY, withTarget } from '@/util';

import {
  applyMoveOffsetToBox,
  getIntersectObjectsByNDC,
  getIntersectPlanePointByNDC,
  isEnabled,
  isOtherOperationRunning,
  isTopMode,
  setControlEnabled,
  validMouseEventAndCanvasElement,
} from '../util';

const useDefaultViewTopMove = ({
  setRenderBoxListById,
  disabled,
  isSingleMoveRef,
  isMultiMoveRef,
}: {
  setRenderBoxListById: (boxIdList: string[]) => void;
  disabled: boolean;
  isSingleMoveRef: React.RefObject<boolean>;
  isMultiMoveRef: React.RefObject<boolean>;
}) => {
  const { getMode } = useToolMainCameraStore();
  const { camera, controls } = useThree();
  const { moveBoxList, selectBox, labelingBoxList, selectedLabelingBoxList } =
    useLabelingBoxContext();
  const labelingBoxListRef = useRef<NEW__LabelingBox[]>([]);
  const selectedLabelingBoxListRef = useRef<NEW__LabelingBox[]>([]);

  const { isSelectedByToolName } = useToolMenuStore();
  const isAiAssistRunning = isSelectedByToolName('aiAssist');

  const prepareSingleMove = ({
    toRenderBoxIdList,
  }: {
    toRenderBoxIdList: string[];
  }) => {
    setControlEnabled(false, controls);
    setRenderBoxListById(toRenderBoxIdList);
    isSingleMoveRef.current = true;
    isMultiMoveRef.current = false;
  };

  const cleanupSingleMove = ({ toSelectBoxId }: { toSelectBoxId: string }) => {
    if (!isOtherOperationRunning(isMultiMoveRef.current))
      selectBox(toSelectBoxId);
    useEditingLabelBoxStore.getState().clear();
    setControlEnabled(true, controls);
    setRenderBoxListById([]);
    isSingleMoveRef.current = false;
  };

  const cleanupOnInvalidStream = () => {
    isSingleMoveRef.current = false;
    if (isOtherOperationRunning(isMultiMoveRef.current)) return;

    useEditingLabelBoxStore.getState().clear();
    setRenderBoxListById([]);
    !isOtherOperationRunning(isAiAssistRunning) &&
      setControlEnabled(true, controls);
  };

  useSubscriptions(
    () => [
      fromEvent<PointerEvent>(window, 'pointerdown')
        .pipe(
          filter(() => isEnabled(disabled)),
          filter(() => isTopMode(getMode())),
          filter(() => !isOtherOperationRunning(isAiAssistRunning)),
          filter(
            () =>
              !useEditingLabelBoxStore.getState().isTransforming &&
              !useEditingLabelBoxStore.getState().isResizing,
          ),
          filter(
            e =>
              validMouseEventAndCanvasElement(e) ||
              (e.target instanceof HTMLDivElement &&
                !!e.target.ariaLabel?.match(/box-track-id/)),
          ),
          withTarget,
          withCanvasXY,
          map(e => {
            const intersections = getIntersectObjectsByNDC({
              ndc: new Vector2(e.canvasX, e.canvasY),
              camera,
              objects: labelingBoxListRef.current.map(v => v.boxMesh),
            });

            return { event: e, intersection: intersections[0] };
          }),
          filter(
            ({ intersection }) =>
              !!intersection && !!intersection.object.userData.id,
          ),
          filter(
            ({ intersection }) =>
              !selectedLabelingBoxListRef.current.find(
                box => box.id === intersection.object.userData.id,
              ),
          ),
          map(({ intersection, event }) => {
            const mouseDownPosition = getIntersectPlanePointByNDC({
              ndc: new Vector2(event.canvasX, event.canvasY),
              camera,
              coplanarPoint: intersection.point,
            });

            return { mouseDownPosition, intersection };
          }),
          tap(({ intersection }) => {
            prepareSingleMove({
              toRenderBoxIdList: [intersection.object.userData.id as string],
            });
          }),
          switchMap(
            ({
              intersection,
              mouseDownPosition,
            }: {
              intersection: Intersection;
              mouseDownPosition: Vector3;
            }) =>
              fromEvent<PointerEvent>(window, 'pointermove').pipe(
                withTarget,
                filterOrRestart(e => e.target instanceof HTMLCanvasElement),
                withCanvasXY,
                map(mouseMoveEvent => {
                  const mouseMovePosition = getIntersectPlanePointByNDC({
                    ndc: new Vector2(
                      mouseMoveEvent.canvasX,
                      mouseMoveEvent.canvasY,
                    ),
                    camera,
                    coplanarPoint: intersection.point,
                  });

                  return {
                    mouseMovePosition,
                  };
                }),
                map(({ mouseMovePosition }) => {
                  const moveOffset = subVector(
                    mouseMovePosition,
                    mouseDownPosition,
                  );

                  return {
                    moveOffset,
                    intersection,
                  };
                }),
                filterOrRestart(
                  ({ moveOffset }) =>
                    !isNaN(moveOffset.x) && !isNaN(moveOffset.y),
                ),
                tap(({ moveOffset }) => {
                  useEditingLabelBoxStore
                    .getState()
                    .setMoveOffset([moveOffset.x, moveOffset.y, 0]);
                }),
                takeUntil(fromEvent<PointerEvent>(window, 'pointerup')),
                defaultIfEmpty({ moveOffset: new Vector3() }),
                last(),
                tap(({ moveOffset }) => {
                  const interactionTargetLabelingBox = intersection.object;
                  const orgPostion = intersection.object.position.clone();

                  moveBoxList([
                    applyMoveOffsetToBox(
                      moveOffset,
                      orgPostion,
                    )(interactionTargetLabelingBox.userData.id),
                  ]);

                  selectBox(intersection.object.userData.id);
                }),
                observeOn(animationFrameScheduler),
                finalize(() => {
                  cleanupSingleMove({
                    toSelectBoxId: intersection.object.userData.id,
                  });
                }),
              ),
          ),
          finalize(cleanupOnInvalidStream),
        )
        .subscribe(),
    ],
    [camera, controls, disabled, isAiAssistRunning],
  );

  useEffect(() => {
    labelingBoxListRef.current = labelingBoxList;
  }, [labelingBoxList]);

  useEffect(() => {
    selectedLabelingBoxListRef.current = selectedLabelingBoxList;
  }, [selectedLabelingBoxList]);
};

export default useDefaultViewTopMove;
